import { FONT_CATALOG, fontInfo, hasThai, THAI_FALLBACK } from '../fonts/catalog';

/*
 * Rules for the print-shop PDF, kept free of DOM/Fabric/WASM so they can be unit-tested.
 *
 *   sizes and boxes     page = trim + bleed + room for crop marks; design px are 300 DPI
 *   fonts               which bundled TTF (public/fonts) draws a family/weight, CSS matching rules
 *   colours             parse CSS colours; pure black prints as K only
 *   what can't be drawn as vector shapes → drawn as a 300 DPI picture (rasterReason)
 *   preflight           the "ตรวจก่อนส่ง" list shown before downloading
 */

/* ---------- sizes and boxes ---------- */

export const PRINT_DPI = 300;
export const MM_PER_INCH = 25.4;
export const BLEED_CHOICES = [0, 3, 5] as const;
export const MAX_BLEED_MM = 10;
export const DEFAULT_BLEED_MM = 3;
/** inner margin shown in the editor: keep text inside it */
export const SAFE_MM = 3;
export const MARK_LENGTH_MM = 5;
/** crop marks start this far from the trim, or at the bleed edge if that is further out */
export const MARK_MIN_OFFSET_MM = 2;
export const MARK_LINE_PT = 0.25;

export const pxToPt = (px: number) => px / PRINT_DPI * 72;
export const mmToPt = (mm: number) => mm / MM_PER_INCH * 72;
export const mmToPx = (mm: number) => mm / MM_PER_INCH * PRINT_DPI;
export const pxToMm = (px: number) => px / PRINT_DPI * MM_PER_INCH;

/** Returns a Thai error, or null when the bleed is allowed. */
export function checkBleed(mm: unknown): string | null {
  if (typeof mm !== 'number' || !Number.isFinite(mm)) return 'ระยะตัดตกต้องเป็นตัวเลข (มิลลิเมตร)';
  if (mm < 0 || mm > MAX_BLEED_MM) return `ระยะตัดตกต้องอยู่ระหว่าง 0 ถึง ${MAX_BLEED_MM} mm`;
  return null;
}

/** The bleed saved in a design JSON under `print` (undefined when it was never set). */
export function bleedIn(json: string): number | undefined {
  try {
    const v = (JSON.parse(json) as { print?: { bleedMm?: unknown } }).print?.bleedMm;
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= MAX_BLEED_MM ? v : undefined;
  } catch { return undefined; }
}

export interface Box { x: number; y: number; w: number; h: number }
export interface PageBoxes {
  /** whole sheet in points */
  mediaW: number; mediaH: number;
  trim: Box; bleed: Box;
  /** design px → page points: X = a*x + e, Y = d*y + f (PDF y goes up) */
  toPage: [number, number, number, number, number, number];
  /** distance from the trim edge to where crop marks start, in points */
  markOffset: number;
  bleedPx: number;
}

/**
 * Page layout for a design of w × h px with `bleedMm` on every side.
 * `marks: false` (document PDF): the sheet is exactly the design, nothing around it.
 */
export function pageBoxes(wPx: number, hPx: number, bleedMm: number, marks = true): PageBoxes {
  const b = mmToPt(bleedMm);
  const markOffset = mmToPt(Math.max(bleedMm, MARK_MIN_OFFSET_MM));
  const margin = marks ? markOffset + mmToPt(MARK_LENGTH_MM + 1) : b;   // trim → sheet edge
  const tw = pxToPt(wPx), th = pxToPt(hPx);
  const mediaW = tw + 2 * margin, mediaH = th + 2 * margin;
  const k = 72 / PRINT_DPI;
  return {
    mediaW, mediaH,
    trim: { x: margin, y: margin, w: tw, h: th },
    bleed: { x: margin - b, y: margin - b, w: tw + 2 * b, h: th + 2 * b },
    toPage: [k, 0, 0, -k, margin, mediaH - margin],
    markOffset,
    bleedPx: mmToPx(bleedMm)
  };
}

/** Eight short lines at the corners of the trim, outside the bleed. [x1, y1, x2, y2] in points. */
export function cropMarks(p: PageBoxes): Array<[number, number, number, number]> {
  const { x, y, w, h } = p.trim, o = p.markOffset, L = mmToPt(MARK_LENGTH_MM);
  const out: Array<[number, number, number, number]> = [];
  for (const cx of [x, x + w]) for (const cy of [y, y + h]) {
    const sx = cx === x ? -1 : 1, sy = cy === y ? -1 : 1;
    out.push([cx + sx * o, cy, cx + sx * (o + L), cy]);   // horizontal, level with the trim edge
    out.push([cx, cy + sy * o, cx, cy + sy * (o + L)]);   // vertical
  }
  return out;
}

/**
 * Text for a plain PDF string (identifiers): printable ASCII only, without ( ) \\ that end or escape it.
 * pdf-lib's PDFString.of() writes each character as one byte, unescaped — Thai would break the file.
 * Text people read (titles, profile names) goes through PDFHexString.fromText() instead.
 */
export const asciiId = (s: string) => s.replace(/[^\x20-\x7E]/g, '').replace(/[()\\]/g, '').replace(/\s+/g, ' ').trim();

/* ---------- fonts ---------- */

/** The fonts the editor offers, each with its real weights, bundled as TTF (src/fonts/catalog.ts is the one list). */
export const FONT_FILES: Record<string, { slug: string; weights: number[] }> =
  Object.fromEntries(FONT_CATALOG.map(f => [f.family, { slug: f.slug, weights: f.weights }]));

/** CSS font-weight value → number ('bold' → 700, 'normal' → 400). */
export function weightOf(v: unknown): number {
  if (v === 'bold' || v === 'bolder') return 700;
  if (v === undefined || v === null || v === '' || v === 'normal' || v === 'lighter') return 400;
  const n = Number(v);
  return Number.isFinite(n) && n >= 1 && n <= 1000 ? n : 400;
}

/** The weight a browser picks from `available` for `desired` (CSS Fonts 4 §5.2 font-weight matching). */
export function matchWeight(available: number[], desired: number): number {
  if (available.includes(desired)) return desired;
  const up = available.filter(w => w > desired).sort((a, b) => a - b);
  const down = available.filter(w => w < desired).sort((a, b) => b - a);
  if (desired >= 400 && desired <= 500) {
    const toFive = up.filter(w => w <= 500);
    return toFive[0] ?? down[0] ?? up[0];
  }
  return desired < 400 ? (down[0] ?? up[0]) : (up[0] ?? down[0]);
}

/** URL of the TTF that draws this family/weight, or null when the family has no bundled file. */
export function fontFileFor(family: unknown, weight: unknown): { url: string; weight: number } | null {
  const f = FONT_FILES[String(family ?? '').replace(/^["']|["']$/g, '')];
  if (!f) return null;
  const w = matchWeight(f.weights, weightOf(weight));
  return { url: `/fonts/${f.slug}-${w}.ttf`, weight: w };
}

/* ---------- colours ---------- */

export interface Rgba { r: number; g: number; b: number; a: number }

const NAMED: Record<string, Rgba> = {
  black: { r: 0, g: 0, b: 0, a: 1 }, white: { r: 255, g: 255, b: 255, a: 1 }, transparent: { r: 0, g: 0, b: 0, a: 0 }
};

/** #rgb, #rgba, #rrggbb, #rrggbbaa, rgb(), rgba(), black/white/transparent. Anything else → null. */
export function parseColor(v: unknown): Rgba | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (NAMED[s]) return { ...NAMED[s] };
  let m = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(s);
  if (m) {
    let h = m[1];
    if (h.length <= 4) h = [...h].map(c => c + c).join('');
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: h.length === 8 ? n(6) / 255 : 1 };
  }
  m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(s);
  if (m) {
    const c = (x: string) => Math.max(0, Math.min(255, Math.round(Number(x))));
    const a = m[4] === undefined ? 1 : Math.max(0, Math.min(1, Number(m[4])));
    return { r: c(m[1]), g: c(m[2]), b: c(m[3]), a };
  }
  return null;
}

/** Pure black prints with black ink only (0/0/0/100), so thin text and lines have no colour fringes. */
export const isPureBlack = (c: Rgba) => c.r === 0 && c.g === 0 && c.b === 0;

/** Very dark grey that looks black on screen but prints with all four inks. */
export const isNearBlack = (c: Rgba) => !isPureBlack(c)
  && Math.max(c.r, c.g, c.b) <= 48 && Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b) <= 24;

/* ---------- objects (as saved in the design JSON) ---------- */

export interface PlainObject {
  type?: string; left?: number; top?: number; width?: number; height?: number;
  scaleX?: number; scaleY?: number; angle?: number; skewX?: number; skewY?: number; flipX?: boolean; flipY?: boolean;
  originX?: string; originY?: string; visible?: boolean; opacity?: number; name?: string;
  fill?: unknown; stroke?: unknown; strokeWidth?: number; strokeUniform?: boolean; shadow?: unknown;
  clipPath?: unknown; globalCompositeOperation?: string; path?: unknown;
  text?: string; fontFamily?: string; fontWeight?: unknown; fontStyle?: string; styles?: unknown;
  underline?: boolean; linethrough?: boolean; overline?: boolean; textBackgroundColor?: unknown; direction?: string;
  startAngle?: number; endAngle?: number; rx?: number; ry?: number; src?: string; cropX?: number; cropY?: number;
  x1?: number; y1?: number; x2?: number; y2?: number;
  /** a group (กราฟิก 0019: an SVG put in as shapes): its shapes */
  objects?: PlainObject[];
  /** เอฟเฟกต์ข้อความ (src/editor/textFx.ts) */
  textFx?: unknown;
}

export const VECTOR_TYPES = ['rect', 'circle', 'ellipse', 'triangle', 'line', 'polygon', 'polyline', 'path', 'thaiTextbox', 'textbox', 'i-text', 'text'];
export const isText = (o: PlainObject) => ['thaiTextbox', 'textbox', 'i-text', 'text'].includes(o.type ?? '');

function hasStyles(styles: unknown): boolean {
  if (!styles) return false;
  if (Array.isArray(styles)) return styles.length > 0;
  if (typeof styles === 'object') return Object.values(styles as object).some(line => line && Object.keys(line).length > 0);
  return false;
}

const paintOk = (p: unknown) => p === undefined || p === null || p === '' || parseColor(p) !== null;

/**
 * Why this object cannot be drawn as vector shapes (it is then drawn as a 300 DPI CMYK picture),
 * or null when it can. Images are always pictures and are not "fallbacks", so they return null.
 */
export function rasterReason(o: PlainObject): string | null {
  const t = o.type ?? '';
  if (t === 'image') {
    if (o.shadow) return 'มีเงา';
    if (o.clipPath) return 'มีการครอบตัดรูปทรง';
    return null;
  }
  // กราฟิก (0019): vector when every shape in it is (drawn one by one, src/print/pdf.ts drawVector)
  if (t === 'group') {
    const kids = o.objects ?? [];
    if (!kids.length) return 'กราฟิกนี้ไม่มีรูปทรงข้างใน';
    if (o.shadow) return 'มีเงา';
    if (o.clipPath) return 'มีการครอบตัดรูปทรง';
    if (o.globalCompositeOperation && o.globalCompositeOperation !== 'source-over') return 'ใช้โหมดผสมสี';
    // see-through as a whole: on screen the shapes are drawn together first, overlaps would look different drawn one by one
    if ((o.opacity ?? 1) < 1 && kids.length > 1) return 'กราฟิกหลายชิ้นที่ปรับความทึบทั้งชิ้น';
    for (const k of kids) {
      if (k.type === 'image') return 'มีภาพอยู่ในกราฟิก';
      const why = rasterReason(k);
      if (why) return why;
    }
    return null;
  }
  if (!VECTOR_TYPES.includes(t)) return `ชนิด "${t || 'ไม่ทราบ'}" ยังทำเป็นเส้นไม่ได้`;
  // เอฟเฟกต์ข้อความ: นีออน is a glow (a shadow without offset) — named as the person chose it
  if (o.shadow) return (o.textFx as { kind?: unknown } | undefined)?.kind === 'neon' ? 'มีเอฟเฟกต์นีออน (เรืองแสง)' : 'มีเงา';
  if (o.clipPath) return 'มีการครอบตัดรูปทรง';
  if (o.globalCompositeOperation && o.globalCompositeOperation !== 'source-over') return 'ใช้โหมดผสมสี';
  if (!paintOk(o.fill)) return 'สีพื้นเป็นแบบไล่สีหรือลวดลาย';
  if (!paintOk(o.stroke)) return 'สีเส้นเป็นแบบไล่สีหรือลวดลาย';
  if (t === 'circle' && ((o.startAngle ?? 0) !== 0 || ((o.endAngle ?? 360) % 360) !== 0)) return 'เป็นวงกลมไม่เต็มวง';
  if (isText(o)) {
    if (hasStyles(o.styles)) return 'ตัวหนังสือมีหลายแบบในกล่องเดียว';
    if (o.underline || o.linethrough || o.overline) return 'มีขีดเส้นใต้หรือขีดฆ่า';
    if (o.textBackgroundColor) return 'มีสีพื้นหลังตัวหนังสือ';
    if (o.path) return 'ตัวหนังสือวิ่งตามเส้น';
    if (o.direction === 'rtl') return 'ตัวหนังสือขวาไปซ้าย';
    if (o.fontStyle && o.fontStyle !== 'normal') return 'ตัวเอียง (ไม่มีไฟล์ฟอนต์ตัวเอียง)';
    if (!fontFileFor(o.fontFamily, o.fontWeight)) return `ฟอนต์ "${o.fontFamily ?? ''}" ไม่มีไฟล์สำหรับแปลงเป็นเส้น`;
    // a font without Thai letters: on screen the Thai is drawn with THAI_FALLBACK, which one outline file cannot do
    const info = fontInfo(o.fontFamily);
    if (info && !info.thai && hasThai(o.text)) return `ฟอนต์ "${info.family}" ไม่มีตัวไทย (ตัวไทยใช้ ${THAI_FALLBACK} แทน)`;
  }
  return null;
}

type M = [number, number, number, number, number, number];
const mul = (a: M, b: M): M => [
  a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1],
  a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3],
  a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5]
];
const ORIGIN: Record<string, number> = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };

/**
 * Size of the object's own box before scaling. `swBox` is the outline width Fabric adds when it places
 * the object (it counts strokeWidth even when there is no outline); `swDrawn` is the outline really drawn.
 */
function ownSize(o: PlainObject) {
  const w = o.width ?? 0, h = o.height ?? 0;
  let bw = w, bh = h;
  const sw = o.strokeWidth ?? 1;
  if (o.type === 'line' && (o as { strokeLineCap?: string }).strokeLineCap === 'butt') {
    if (w === 0) bh -= sw;
    if (h === 0) bw -= sw;
  }
  return { w, h, bw, bh, swBox: sw, swDrawn: o.stroke ? sw : 0 };
}

/** The object's transform (object-centre space → design px), the same way Fabric composes it. */
export function objectMatrix(o: PlainObject): M {
  const { w, h, bw: ow, bh: oh, swBox } = ownSize(o);
  const sx = (o.scaleX ?? 1) * (o.flipX ? -1 : 1), sy = (o.scaleY ?? 1) * (o.flipY ? -1 : 1);
  // Fabric places the origin point using the scaled box including the outline (getScaledWidth)
  const bw = o.strokeUniform ? w * Math.abs(sx) + swBox : (ow + swBox) * Math.abs(sx);
  const bh = o.strokeUniform ? h * Math.abs(sy) + swBox : (oh + swBox) * Math.abs(sy);
  const ox = ORIGIN[o.originX ?? 'left'] ?? 0, oy = ORIGIN[o.originY ?? 'top'] ?? 0;
  const dx = (0.5 - ox) * bw, dy = (0.5 - oy) * bh;
  const r = (o.angle ?? 0) * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
  const cx = (o.left ?? 0) + dx * c - dy * s, cy = (o.top ?? 0) + dx * s + dy * c;
  const skx = Math.tan((o.skewX ?? 0) * Math.PI / 180), sky = Math.tan((o.skewY ?? 0) * Math.PI / 180);
  // translate · rotate · scale · skewX · skewY, as fabric.util.composeMatrix
  return mul(mul(mul([c, s, -s, c, cx, cy], [sx, 0, 0, sy, 0, 0]), [1, 0, skx, 1, 0, 0]), [1, sky, 0, 1, 0, 0]);
}

/** Axis-aligned bounds in design px (stroke included). */
export function objectBounds(o: PlainObject): Box {
  const { w, h, swDrawn: sw } = ownSize(o);
  const m = objectMatrix(o);
  const hw = w / 2, hh = h / 2;
  const pts = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([x, y]) => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]);
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  // half the outline sticks out on every side; scaled with the object unless it is uniform
  const half = sw / 2 * (o.strokeUniform ? 1 : Math.max(Math.abs(o.scaleX ?? 1), Math.abs(o.scaleY ?? 1)));
  const x = Math.min(...xs) - half, y = Math.min(...ys) - half;
  return { x, y, w: Math.max(...xs) + half - x, h: Math.max(...ys) + half - y };
}

/* ---------- preflight ---------- */

export type CheckKind = 'warn' | 'info' | 'ok';
/** `page` and `index`: which page (0 = first) and which object on it */
export type CheckAction =
  | { kind: 'select'; page: number; index: number; label: string }
  | { kind: 'extend'; page: number; index: number; label: string }
  | { kind: 'black'; items: Array<{ page: number; index: number }>; label: string }
  /** `pages`: the pages without bleed (all of them when every page has none) */
  | { kind: 'bleed'; mm: number; pages: number[]; label: string };
export interface Check { kind: CheckKind; title: string; text: string; action?: CheckAction }

export const LOW_PPI = 300;
/** document PDF: pictures are reduced to this (enough for an office printer, small enough for LINE/e-mail) */
export const DOC_PPI = 200;
/** document PDF: below this a picture is warned about */
export const DOC_LOW_PPI = 150;
/** an edge this close inside the trim is taken as "meant to reach the edge" */
export const EDGE_TOLERANCE_MM = 1;

/**
 * Thai vowel and tone marks that sit on the letter before them (never the first character of a piece).
 * U+0E33 "ำ" is a spacing vowel in Unicode, but its circle sits on the letter before it: "น้|ำ" read "น้…" (BUG-47).
 */
const THAI_MARK = /[\u0E31\u0E33-\u0E3A\u0E47-\u0E4E]/;

/**
 * The first `max` characters for a label, never cutting a tone mark or vowel off its letter
 * ("ไฟล์" stays "ไฟล์", not "ไฟล"), with "…" when something was left out.
 */
export function shortText(text: string, max: number): string {
  const t = text.replace(/[\u200B\u2060]/g, '').replace(/\s+/g, ' ').trim();
  const chars = Array.from(t);
  if (chars.length <= max) return t;
  let end = max;
  while (end < chars.length && THAI_MARK.test(chars[end])) end++;
  return end >= chars.length ? t : chars.slice(0, end).join('') + '…';
}

/** How an object is named in messages: its layer name, else the start of its text, else its position. */
export const nameOf = (o: PlainObject, i: number) => (o.name && String(o.name).trim())
  || (isText(o) ? `ข้อความ "${shortText(String(o.text ?? ''), 16)}"` : `ชิ้นที่ ${i + 1}`);

/**
 * Effective resolution of a picture at its printed size (pixels per inch). One source pixel covers
 * `scale` design px and design px are 300 DPI, so the picture prints at 300 / scale.
 */
export function effectivePpi(o: PlainObject): number {
  const sx = Math.abs(o.scaleX ?? 1), sy = Math.abs(o.scaleY ?? o.scaleX ?? 1);
  const s = Math.max(sx, sy);
  return s > 0 ? PRINT_DPI / s : Infinity;
}

/** Which sides of `b` reach the trim edge but stop short of the bleed edge. */
export function shortSides(b: Box, wPx: number, hPx: number, bleedPx: number): string[] {
  const tol = mmToPx(EDGE_TOLERANCE_MM), eps = 0.5;
  const sides: string[] = [];
  if (b.x <= tol && b.x > -bleedPx + eps) sides.push('ซ้าย');
  if (b.x + b.w >= wPx - tol && b.x + b.w < wPx + bleedPx - eps) sides.push('ขวา');
  if (b.y <= tol && b.y > -bleedPx + eps) sides.push('บน');
  if (b.y + b.h >= hPx - tol && b.y + b.h < hPx + bleedPx - eps) sides.push('ล่าง');
  return sides;
}

const visibleOnPage = (b: Box, w: number, h: number, bleedPx: number) =>
  b.x < w + bleedPx && b.y < h + bleedPx && b.x + b.w > -bleedPx && b.y + b.h > -bleedPx;

/** The "ตรวจก่อนส่ง" list: warnings first, then notes, then what is fine. Nothing here blocks the download. */
/** "หน้า 3: " in front of a title when the design has more than one page */
const onPage = (pages: unknown[], k: number) => pages.length > 1 ? `หน้า ${k + 1}: ` : '';

type PrintSize = { w: number; h: number; bleedMm?: number };
/** one size for every page, or each page's own (หน้าหลายขนาด) */
const sizeAt = <T>(docs: T | T[], k: number): T => Array.isArray(docs) ? docs[k] ?? docs[0] : docs;

/**
 * `pages`: each page's objects (a 1-page design is [objects]); `docs`: one size for all pages or each page's own;
 * `only`: the pages being exported (เลือกหน้า) — the others are not checked, and page numbers stay the design's own
 */
export function preflight(pages: PlainObject[][], docs: PrintSize | PrintSize[], only?: number[]): Check[] {
  if (only) pages = pages.map((p, k) => only.includes(k) ? p : []);
  const warn: Check[] = [], info: Check[] = [], ok: Check[] = [];
  let texts = 0, black = 0;
  const near: Array<{ page: number; index: number }> = [];

  const bare = pages.map((_, k) => k).filter(k => (!only || only.includes(k)) && (sizeAt(docs, k).bleedMm ?? 0) <= 0);
  if (bare.length) {
    const all = bare.length === (only?.length ?? pages.length);
    warn.push({
      kind: 'warn', title: all ? (only && only.length < pages.length ? 'หน้าที่เลือกไม่มีระยะตัดตก' : 'งานนี้ไม่มีระยะตัดตก') : `หน้า ${bare.map(k => k + 1).join(', ')} ไม่มีระยะตัดตก`,
      text: 'โรงพิมพ์ส่วนใหญ่ต้องการภาพเลยขอบตัดออกไป 3 mm ถ้าตัดเหลื่อมจะเห็นขอบขาว',
      action: { kind: 'bleed', mm: DEFAULT_BLEED_MM, pages: bare, label: all ? `ตั้งระยะตัดตก ${DEFAULT_BLEED_MM} mm` : `ตั้งระยะตัดตก ${DEFAULT_BLEED_MM} mm ให้หน้าเหล่านี้` }
    });
  }

  pages.forEach((objects, k) => objects.forEach((o, i) => {
    if (o.visible === false || (o.opacity ?? 1) <= 0) return;
    const doc = { ...sizeAt(docs, k), bleedMm: sizeAt(docs, k).bleedMm ?? 0 };
    const bleedPx = mmToPx(doc.bleedMm);
    const b = objectBounds(o);
    if (!visibleOnPage(b, doc.w, doc.h, bleedPx)) return;
    const name = nameOf(o, i), at = onPage(pages, k);

    if (o.type === 'image') {
      const ppi = Math.round(effectivePpi(o));
      if (ppi < LOW_PPI) warn.push({
        kind: 'warn', title: `${at}รูป "${name}" ความละเอียดต่ำ`,
        text: `${ppi} ppi ที่ขนาดพิมพ์จริง (ควร ${LOW_PPI} ขึ้นไป) อาจดูแตกเป็นเม็ด ส่งออกได้ แต่ควรใช้ไฟล์ที่ใหญ่กว่า`,
        action: { kind: 'select', page: k, index: i, label: 'เลือกรูปนี้' }
      });
    }

    if (doc.bleedMm > 0 && !isText(o)) {
      const sides = shortSides(b, doc.w, doc.h, bleedPx);
      if (sides.length) {
        const gap = Math.max(...sides.map(s => s === 'ซ้าย' ? b.x + bleedPx : s === 'บน' ? b.y + bleedPx
          : s === 'ขวา' ? doc.w + bleedPx - (b.x + b.w) : doc.h + bleedPx - (b.y + b.h)));
        const plainRect = o.type === 'rect' && !(o.angle ?? 0) && !(o.skewX ?? 0) && !(o.skewY ?? 0);
        warn.push({
          kind: 'warn', title: `${at}"${name}" ไม่ถึงขอบตัดตก`,
          text: `ด้าน${sides.join(' ')} ขาดอีก ${pxToMm(gap).toFixed(1)} mm ถ้าตัดเหลื่อมจะเห็นขอบขาว`,
          action: plainRect ? { kind: 'extend', page: k, index: i, label: 'ขยายให้ถึงขอบ' } : { kind: 'select', page: k, index: i, label: 'เลือกชิ้นนี้' }
        });
      }
    }

    const why = rasterReason(o);
    if (why) info.push({
      kind: 'info', title: `${at}"${name}" จะเป็นภาพ 300 DPI`,
      text: `${why} ทำเป็นเส้นไม่ได้ ระบบจะแปลงชิ้นนี้เป็นภาพ CMYK ความละเอียด 300 DPI`,
      action: { kind: 'select', page: k, index: i, label: 'เลือกชิ้นนี้' }
    });
    else if (isText(o)) {
      texts++;
      const c = parseColor(o.fill);
      if (c && isPureBlack(c)) black++;
      else if (c && isNearBlack(c)) near.push({ page: k, index: i });
    }
  }));

  if (near.length) info.push({
    kind: 'info', title: `ตัวหนังสือสีเกือบดำ ${near.length} กล่อง จะพิมพ์ด้วยหมึก 4 สี`,
    text: 'ตัวหนังสือเล็กที่พิมพ์ 4 สีอาจเห็นขอบสีเหลื่อม ถ้าเปลี่ยนเป็นดำล้วนจะพิมพ์ด้วยหมึกดำสีเดียว (สีบนจอเข้มขึ้นเล็กน้อย)',
    action: { kind: 'black', items: near, label: 'เปลี่ยนเป็นดำล้วน' }
  });
  if (texts) ok.push({
    kind: 'ok', title: `ตัวหนังสือ ${texts} กล่อง`,
    text: `แปลงเป็นเส้น โรงพิมพ์ไม่ต้องมีฟอนต์${black ? ` · สีดำล้วน ${black} กล่อง พิมพ์ด้วยหมึกดำสีเดียว (0/0/0/100)` : ''}`
  });
  if (!warn.length) ok.unshift({ kind: 'ok', title: 'ไม่พบปัญหาที่ต้องแก้', text: 'ส่งออกได้เลย' });
  return [...warn, ...info, ...ok];
}

/**
 * The check list of the document PDF (A4 for e-mail/LINE/office printer): no bleed or ink checks,
 * only what changes how it looks: pictures reduced or too coarse, pieces that become pictures.
 */
export function preflightDoc(pages: PlainObject[][], docs: PrintSize | PrintSize[], only?: number[]): Check[] {
  if (only) pages = pages.map((p, k) => only.includes(k) ? p : []);
  const warn: Check[] = [], info: Check[] = [], ok: Check[] = [];
  let texts = 0, reduced = 0;
  pages.forEach((objects, k) => objects.forEach((o, i) => {
    if (o.visible === false || (o.opacity ?? 1) <= 0) return;
    const doc = sizeAt(docs, k);
    const b = objectBounds(o);
    if (!visibleOnPage(b, doc.w, doc.h, 0)) return;
    const name = nameOf(o, i), at = onPage(pages, k);
    if (o.type === 'image') {
      const ppi = Math.round(effectivePpi(o));
      if (ppi < DOC_LOW_PPI) warn.push({
        kind: 'warn', title: `${at}รูป "${name}" ความละเอียดต่ำ`,
        text: `${ppi} ppi ที่ขนาดจริง (ควร ${DOC_LOW_PPI} ขึ้นไป) พิมพ์ออกมาอาจดูแตก ส่งออกได้ แต่ควรใช้ไฟล์ที่ใหญ่กว่า`,
        action: { kind: 'select', page: k, index: i, label: 'เลือกรูปนี้' }
      });
      else if (ppi > DOC_PPI) reduced++;
    }
    const why = rasterReason(o);
    if (why) info.push({
      kind: 'info', title: `${at}"${name}" จะเป็นภาพ ${DOC_PPI} DPI`,
      text: `${why} ทำเป็นเส้นไม่ได้ ระบบจะแปลงชิ้นนี้เป็นภาพ${isText(o) ? ' (ตัวหนังสือในชิ้นนี้คัดลอกไม่ได้)' : ''}`,
      action: { kind: 'select', page: k, index: i, label: 'เลือกชิ้นนี้' }
    });
    else if (isText(o)) texts++;
  }));
  if (reduced) info.push({
    kind: 'info', title: `ย่อรูป ${reduced} รูปเหลือ ${DOC_PPI} ppi`,
    text: 'พอสำหรับเอกสารและเครื่องพิมพ์สำนักงาน ทำให้ไฟล์เล็ก ส่งทางอีเมลหรือไลน์ได้ (ถ้าต้องการคมสุดให้ใช้ PDF โรงพิมพ์)'
  });
  if (texts) ok.push({ kind: 'ok', title: `ตัวหนังสือ ${texts} กล่อง`, text: 'คมชัดทุกขนาด คัดลอกและค้นหาในไฟล์ได้' });
  if (!warn.length) ok.unshift({ kind: 'ok', title: 'ไม่พบปัญหาที่ต้องแก้', text: 'ส่งออกได้เลย' });
  return [...warn, ...info, ...ok];
}
